import { Eye, Icon } from "@norican/icons";
import { Button } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { useStopViewAs } from "./api";
import { cookieViewAs, msUntilViewAsEnds } from "./view-as-state";

/**
 * The bright strip on every page while an admin views as someone (6 Oct):
 * "Viewing as Anna Berg (anna.berg@…) — read-only · Stop".
 *
 * Above everything else in the sticky header, so it cannot scroll away. When
 * the view runs out (an hour at most) every read is reset, which brings the
 * admin's own view back without a click.
 */
export function ViewAsBanner() {
  const { t } = useT();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const stop = useStopViewAs();
  // A demo persona tab (19 Oct) has its own coloured banner instead.
  const view = cookieViewAs(me);

  useEffect(() => {
    if (view === null) {
      return;
    }
    const timer = window.setTimeout(
      () => void queryClient.resetQueries(),
      msUntilViewAsEnds(view) + 1_000,
    );
    return () => window.clearTimeout(timer);
  }, [view, queryClient]);

  if (view === null) {
    return null;
  }
  return (
    <div
      role="status"
      aria-label={t("viewAs.label")}
      className="bg-danger text-danger-fg-on-solid w-full"
    >
      <div className="mx-auto flex w-full max-w-[var(--norican-size-container-xl)] flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 sm:px-6 lg:px-8">
        <Icon icon={Eye} size="md" className="shrink-0" />
        <span className="min-w-0 flex-1 text-sm font-semibold">
          {t("viewAs.banner", { name: view.name, email: view.email })}
        </span>
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          className="bg-bg text-fg"
          onClick={() => stop.mutate()}
          disabled={stop.isPending}
        >
          {stop.isPending ? t("viewAs.stopping") : t("viewAs.stop")}
        </Button>
      </div>
    </div>
  );
}
